import React from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import OrganizationsPage from './pages/OrganizationsPage';
import UsersPage from './pages/UsersPage';
import ProjectsPage from './pages/ProjectsPage';
import SchedulesPage from './pages/SchedulesPage';
import AuditorAvailabilityPage from './pages/AuditorAvailabilityPage';
import NotificationsPage from './pages/NotificationsPage';
import EvidenceMonitorPage from './pages/EvidenceMonitorPage';
import GamificationPage from './pages/GamificationPage';
import TemplatesPage from './pages/TemplatesPage';
import ProtectedRoute from './ProtectedRoute';
import { useAuth } from './AuthContext';
import { NotificationsProvider, useNotifications } from './NotificationsContext';

const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { logout } = useAuth();
  const { unreadCount } = useNotifications();
  const location = useLocation();

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const baseLinkStyle: React.CSSProperties = {
    padding: '6px 10px',
    borderRadius: 999,
    textDecoration: 'none',
    color: '#e5e7eb',
    backgroundColor: 'transparent',
  };

  const activeLinkStyle: React.CSSProperties = {
    ...baseLinkStyle,
    backgroundColor: 'rgba(15, 23, 42, 0.9)',
    border: '1px solid rgba(148, 163, 184, 0.6)',
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#020617',
        color: '#e5e7eb',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      }}
    >
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          padding: '10px 24px',
          borderBottom: '1px solid rgba(31, 41, 55, 0.9)',
          background:
            'linear-gradient(to right, rgba(15, 23, 42, 0.98), rgba(15, 23, 42, 0.96))',
          boxShadow: '0 10px 30px rgba(15, 23, 42, 0.85)',
        }}
      >
        <nav
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <Link
            to="/dashboard"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              textDecoration: 'none',
              marginRight: 16,
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 999,
                background:
                  'conic-gradient(from 160deg at 50% 50%, #38bdf8, #4f46e5, #22c55e, #38bdf8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.9)',
              }}
            >
              <span
                style={{
                  fontWeight: 800,
                  color: '#020617',
                  fontSize: 14,
                  letterSpacing: 0.4,
                }}
              >
                H
              </span>
            </div>
            <span
              style={{
                fontWeight: 600,
                fontSize: 15,
                color: '#f9fafb',
              }}
            >
              Heimdall
            </span>
          </Link>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
            }}
          >
            <Link to="/dashboard" style={isActive('/dashboard') ? activeLinkStyle : baseLinkStyle}>
              Escritorio
            </Link>
            <Link
              to="/organizations"
              style={isActive('/organizations') ? activeLinkStyle : baseLinkStyle}
            >
              Organizaciones
            </Link>
            <Link to="/users" style={isActive('/users') ? activeLinkStyle : baseLinkStyle}>
              Usuarios
            </Link>
            <Link
              to="/projects"
              style={isActive('/projects') ? activeLinkStyle : baseLinkStyle}
            >
              Proyectos
            </Link>
            <Link
              to="/templates"
              style={isActive('/templates') ? activeLinkStyle : baseLinkStyle}
            >
              Plantillas
            </Link>
            <Link to="/agenda" style={isActive('/agenda') ? activeLinkStyle : baseLinkStyle}>
              Agenda
            </Link>
            <Link
              to="/availability"
              style={isActive('/availability') ? activeLinkStyle : baseLinkStyle}
            >
              Disponibilidad
            </Link>
            <Link
              to="/evidences-monitor"
              style={isActive('/evidences-monitor') ? activeLinkStyle : baseLinkStyle}
            >
              Evidencias
            </Link>
            <Link
              to="/gamification"
              style={isActive('/gamification') ? activeLinkStyle : baseLinkStyle}
            >
              Gamificación
            </Link>
            <Link
              to="/notifications"
              style={
                isActive('/notifications')
                  ? { ...activeLinkStyle, position: 'relative' }
                  : { ...baseLinkStyle, position: 'relative' }
              }
            >
              Notificaciones
              {unreadCount !== null && unreadCount > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: -3,
                    right: -4,
                    minWidth: 16,
                    height: 16,
                    borderRadius: 999,
                    backgroundColor: '#ef4444',
                    color: '#fef2f2',
                    fontSize: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 4px',
                  }}
                >
                  {unreadCount}
                </span>
              )}
            </Link>
          </div>

          <button
            onClick={logout}
            style={{
              marginLeft: 'auto',
              padding: '6px 12px',
              borderRadius: 999,
              border: '1px solid rgba(148, 163, 184, 0.7)',
              backgroundColor: 'transparent',
              color: '#e5e7eb',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Logout
          </button>
        </nav>
      </header>

      <main style={{ padding: '16px 24px 24px' }}>{children}</main>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <NotificationsProvider>
        <Routes>
          <Route path="/login" element={<Login />} />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Dashboard />
                </AppLayout>
              </ProtectedRoute>
            }
          />
        <Route
          path="/organizations"
          element={
            <ProtectedRoute>
              <AppLayout>
                <OrganizationsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/gamification"
          element={
            <ProtectedRoute>
              <AppLayout>
                <GamificationPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <AppLayout>
                <UsersPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ProjectsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/templates"
          element={
            <ProtectedRoute>
              <AppLayout>
                <TemplatesPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/agenda"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SchedulesPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/availability"
          element={
            <ProtectedRoute>
              <AppLayout>
                <AuditorAvailabilityPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <AppLayout>
                <NotificationsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/evidences-monitor"
          element={
            <ProtectedRoute>
              <AppLayout>
                <EvidenceMonitorPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="*"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Dashboard />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        </Routes>
      </NotificationsProvider>
    </BrowserRouter>
  );
};

export default App;
